import { useMemo } from "react";
import { defineMessages } from "react-intl";
import { isEmpty } from "lodash";

import { SORTABLE_LIST } from "../SchemaTypes";
import Field, { FIELD_MODE_FIELDSET } from "../Field";
import SortableListInput from "../../Input/SortableListInput";
import { useParsedFieldProps } from "./useParsedFieldProps";
import { useFormatMessage } from "../../Intl/Context";
import { useForm } from "../withFormStore";
import getFormFieldValue from "../../../helpers/getFormFieldValue";

const SortableListField = props => {
  const { fieldProps, inputProps } = useParsedFieldProps(props);
  const {
    label,
    fieldName,
    options,
    required,
    minimumSelectableOptions,
    maximumSelectableOptions
  } = props;

  const { formState } = useForm();
  const currentValue = getFormFieldValue(formState, fieldName) || [];

  const maximumOptions = useMemo(
    () =>
      maximumSelectableOptions &&
      maximumSelectableOptions !== 0 &&
      maximumSelectableOptions < options.length
        ? maximumSelectableOptions
        : null,
    [maximumSelectableOptions, options.length]
  );

  const minimumOptions = useMemo(() => {
    if (minimumSelectableOptions && minimumSelectableOptions > 0) {
      return minimumSelectableOptions;
    }

    if (maximumOptions) return 1;

    return null;
  }, [maximumOptions, minimumSelectableOptions]);

  const hasMinMaxConstraints = minimumOptions || maximumOptions;
  const hasSameMinAndMaxConstraints =
    hasMinMaxConstraints && minimumOptions === maximumOptions;
  const requiredValidationNeeded =
    required && (!hasMinMaxConstraints || isEmpty(currentValue));

  const t = useFormatMessage();
  const requiredErrorMessage = t(intlMessages.requiredMessage);
  const lengthValidationMessage = t(intlMessages.lengthValidationMessage, {
    minimum: minimumOptions,
    maximum: maximumOptions
  });
  const singleLengthValidationMessage = t(intlMessages.singleLengthValidationMessage, {
    minimum: minimumOptions,
    maximum: maximumOptions
  });
  const lengthTooShortMessage = t(intlMessages.tooShortMessage, {
    count: minimumSelectableOptions
  });
  const lengthTooLongMessage = t(intlMessages.tooLongMessage, {
    count: maximumSelectableOptions
  });

  const lengthMessage = hasSameMinAndMaxConstraints
    ? singleLengthValidationMessage
    : lengthValidationMessage;
  const tooShortMessage = maximumOptions ? lengthMessage : lengthTooShortMessage;
  const tooLongMessage = minimumOptions ? lengthMessage : lengthTooLongMessage;
  const requiredMessage = hasMinMaxConstraints ? tooShortMessage : requiredErrorMessage;

  const schema = {
    type: SORTABLE_LIST,
    validations: {
      required: requiredValidationNeeded && { errorMessage: requiredMessage },
      length: hasMinMaxConstraints && {
        minimum: minimumSelectableOptions,
        maximum: maximumSelectableOptions,
        tooShortMessage,
        tooLongMessage
      }
    }
  };

  return (
    <Field {...fieldProps} schema={schema} fieldMode={FIELD_MODE_FIELDSET}>
      <SortableListInput {...inputProps} label={label} maximumOptions={maximumOptions} />
    </Field>
  );
};

export default SortableListField;

const intlMessages = defineMessages({
  requiredMessage: {
    id: "form.field.sortableList.requiredMessage",
    defaultMessage: "Toutes les options doivent être priorisées."
  },
  lengthValidationMessage: {
    id: "form.field.sortableList.lengthValidationMessage",
    defaultMessage: `
      Vous devez prioriser au minimum 
      {minimum, plural, one {{minimum} option} other {{minimum} options}}
      et au maximum 
      {maximum, plural, one {{maximum} option} other {{maximum} options}}
    `
  },
  singleLengthValidationMessage: {
    id: "form.field.sortableList.singleLengthValidationMessage",
    defaultMessage: `
      Vous devez prioriser un total de 
      {minimum, plural, one {{minimum} option} other {{minimum} options}}
    `
  },
  tooShortMessage: {
    id: "form.field.sortableList.tooShortMessage",
    defaultMessage: `{count, plural,
      one {Vous devez prioriser au minimum {count} option}
      other {Vous devez prioriser au minimum {count} options}
    }`
  },
  tooLongMessage: {
    id: "form.field.sortableList.tooLongMessage",
    defaultMessage: `{count, plural,
      one {Vous devez prioriser au maximum {count} option}
      other {Vous devez prioriser au maxium {count} options}
    }`
  }
});
